import type {
  CompositionV3Ent,
  CompositionV3Filters,
  SentimentV3Filters,
  TransitionsV3Filters,
} from '@revelio/data-access';
import { EntKind, PositionsEntityKind } from '@revelio/data-access';

import type {
  ActivityTaxonomySelectionListKey,
  AllSelectionListsWithSkillTaxonomyKey,
} from '../../../../shared/selection-lists/types';
import { getCustomSkillLevelId } from './customSkillTaxonomyRegistry';
import { SKILL_REFERENCE_TAXONOMY_TREE_LEVELS } from './skillReferenceTaxonomyTree';

/** ================================
 * ROLE TAXONOMY
 ================================ */
const ROLE_LEVEL_ID_TO_ENT_KIND = {
  role_k7: EntKind.RoleLvl_1,
  role_k50: EntKind.RoleLvl_2,
  role_k150: EntKind.RoleLvl_3,
  role_k500: EntKind.RoleLvl_4,
  role_k1500: EntKind.RoleLvl_5,
  role_k5000: EntKind.RoleLvl_6,
  role_k15000: EntKind.RoleLvl_7,
} as const;

export type RoleTaxonomyLevelId = keyof typeof ROLE_LEVEL_ID_TO_ENT_KIND;

export const ROLE_LEVEL_ID_TO_FILTER_KEY = {
  role_k7: 'role_level_1',
  role_k50: 'role_level_2',
  role_k150: 'role_level_3',
  role_k500: 'role_level_4',
  role_k1500: 'role_level_5',
  role_k5000: 'role_level_6',
  role_k15000: 'role_level_7',
} as const satisfies Readonly<
  Record<RoleTaxonomyLevelId, keyof CompositionV3Filters>
>;

export function isRoleTaxonomyLevelId(id: string): id is RoleTaxonomyLevelId {
  return id in ROLE_LEVEL_ID_TO_FILTER_KEY;
}

export function getEntKindForRoleLevelId(levelId: string): EntKind | null {
  if (!isRoleTaxonomyLevelId(levelId)) {
    return null;
  }
  return ROLE_LEVEL_ID_TO_ENT_KIND[levelId];
}

export function appendRoleSelectionToFilters(
  acc: CompositionV3Filters | TransitionsV3Filters | SentimentV3Filters,
  levelId: string,
  numericId: number
): CompositionV3Filters | TransitionsV3Filters | SentimentV3Filters {
  if (!isRoleTaxonomyLevelId(levelId)) {
    return acc;
  }
  const key = ROLE_LEVEL_ID_TO_FILTER_KEY[levelId];
  const prevRaw = acc[key];
  const prev = Array.isArray(prevRaw) ? prevRaw : [];
  return { ...acc, [key]: [...prev, numericId] };
}

/** ================================
 * SKILL TAXONOMY
 ================================ */
export const appendSkillSelectionToV3Filters = ({
  acc,
  levelId,
  id,
}: {
  acc: CompositionV3Filters | TransitionsV3Filters;
  levelId: string;
  id: number;
}) => {
  const key = getSkillV3FilterKeyForLevelId(levelId);
  if (key === null) return acc;

  const prevRaw = acc[key];
  const prev = Array.isArray(prevRaw) ? prevRaw : [];
  return { ...acc, [key]: [...prev, id] };
};

/**
 * The `skills_level_*` key {@link appendSkillSelectionToV3Filters} files a
 * selection at `levelId` under, or null for a level it does not carry.
 */
export const getSkillV3FilterKeyForLevelId = (
  levelId: string
): SkillslevelFilter | null =>
  isSkillTaxonomyLevelId(levelId)
    ? convertSkillLevelIdToFilterKey(levelId)
    : null;

type SkillLevelId = keyof typeof SKILL_LEVEL_ID_TO_FILTER_KEY;
type SkillslevelFilter = keyof (
  | Pick<
      CompositionV3Filters,
      | 'skills_level_1'
      | 'skills_level_2'
      | 'skills_level_3'
      | 'skills_level_4'
      | 'skills_level_5'
      | 'skills_level_6'
      | 'skills_level_7'
      | 'skills_level_8'
    >
  | Pick<
      TransitionsV3Filters,
      | 'skills_level_1'
      | 'skills_level_2'
      | 'skills_level_3'
      | 'skills_level_4'
      | 'skills_level_5'
      | 'skills_level_6'
      | 'skills_level_7'
      | 'skills_level_8'
    >
);
const convertSkillLevelIdToFilterKey = (
  levelId: SkillLevelId
): SkillslevelFilter | null => {
  const skillLevelIndex = Number(levelId.split('_')[1]);
  if (Number.isNaN(skillLevelIndex)) return null;
  const mappedSkillLevel =
    SKILL_REFERENCE_TAXONOMY_TREE_LEVELS[skillLevelIndex];
  if (mappedSkillLevel === undefined) return null;

  return `skills_level_${mappedSkillLevel}`;
};

/**
 * Tier for each level id until the account's TaxonomyTree fills the registry:
 * the reference ladder that tree is fetched with. A page opened with a skill
 * filter already in its URL builds its pay/postings/talent requests before the
 * tree lands, and the API rejects an id sent at a level it does not exist at
 * (`FuncTaxConv: id (…) not found`). `skill_4` has no reference level — the
 * tree returns four — so it falls back to its ordinal tier.
 */
const SKILL_LEVEL_ID_TO_FALLBACK_TIER = {
  skill_0: SKILL_REFERENCE_TAXONOMY_TREE_LEVELS[0],
  skill_1: SKILL_REFERENCE_TAXONOMY_TREE_LEVELS[1],
  skill_2: SKILL_REFERENCE_TAXONOMY_TREE_LEVELS[2],
  skill_3: SKILL_REFERENCE_TAXONOMY_TREE_LEVELS[3],
  skill_4: 5,
} as const;

/** Maps fallback adaptive skill tree level ids to API `EntKind`. */
const SKILL_LEVEL_ID_TO_ENT_KIND = {
  skill_0: EntKind.SkillLvl_1,
  skill_1: EntKind.SkillLvl_2,
  skill_2: EntKind.SkillLvl_3,
  skill_3: EntKind.SkillLvl_4,
  skill_4: EntKind.SkillLvl_5,
} as const;

type SkillTaxonomyLevelId = keyof typeof SKILL_LEVEL_ID_TO_ENT_KIND;

const SKILL_LEVEL_ID_TO_FILTER_KEY = {
  skill_0: 'skills_level_1',
  skill_1: 'skills_level_2',
  skill_2: 'skills_level_3',
  skill_3: 'skills_level_4',
  skill_4: 'skills_level_5',
} as const satisfies Readonly<
  Record<SkillTaxonomyLevelId, keyof CompositionV3Filters>
>;

function isSkillTaxonomyLevelId(id: string): id is SkillTaxonomyLevelId {
  return id in SKILL_LEVEL_ID_TO_FILTER_KEY;
}

function isCompositionSkillTier(
  tier: number
): tier is 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 {
  return Number.isInteger(tier) && tier >= 1 && tier <= 8;
}

export function getSkillTierForLevelId(levelId: string): number | null {
  if (!isSkillTaxonomyLevelId(levelId)) {
    return null;
  }
  const registryTier = getCustomSkillLevelId(levelId);
  if (registryTier !== undefined && isCompositionSkillTier(registryTier)) {
    return registryTier;
  }
  return SKILL_LEVEL_ID_TO_FALLBACK_TIER[levelId];
}

export function skillEntKindFromTier(tier: number): EntKind {
  switch (tier) {
    case 1:
      return EntKind.SkillLvl_1;
    case 2:
      return EntKind.SkillLvl_2;
    case 3:
      return EntKind.SkillLvl_3;
    case 4:
      return EntKind.SkillLvl_4;
    case 5:
      return EntKind.SkillLvl_5;
    case 6:
      return EntKind.SkillLvl_6;
    case 7:
      return EntKind.SkillLvl_7;
    default:
      return EntKind.SkillLvl_8;
  }
}

export function getEntKindForSkillLevelId(levelId: string): EntKind | null {
  const tier = getSkillTierForLevelId(levelId);
  if (tier === null) {
    return null;
  }
  return skillEntKindFromTier(tier);
}

export function getSkillFilterKeyForLevelId(
  levelId: string
): keyof CompositionV3Filters | null {
  const tier = getSkillTierForLevelId(levelId);
  return getSkillFilterKeyForTier(tier);
}

export function getSkillFilterKeyForTier(
  tier: number | null
): keyof CompositionV3Filters | null {
  if (tier === null) {
    return null;
  }
  if (!isCompositionSkillTier(tier)) {
    return null;
  }
  return `skills_level_${tier}` as keyof CompositionV3Filters;
}

export function appendSkillSelectionToFilters(
  acc: CompositionV3Filters,
  levelId: string,
  numericId: number
): CompositionV3Filters {
  const key = getSkillFilterKeyForLevelId(levelId);
  if (key === null) {
    return acc;
  }
  const prevRaw = acc[key];
  const prev = Array.isArray(prevRaw) ? prevRaw : [];
  return { ...acc, [key]: [...prev, numericId] };
}

/** ================================
 * ACTIVITY TAXONOMY
 ================================ */
/**
 * Activity tree level ids -> `activities_level_*` keys. The granularity ladder
 * is positional (k15 -> k5000 == level 1 -> 6), matching
 * `ACTIVITY_TAXONOMY_TREE_LEVELS`; only a subset of levels is exposed in the
 * picker today, so a selection can carry any of these ids. Composition and
 * transitions name the levels identically, so the intersection below keeps the
 * map usable by both. The `EntKind` counterpart below carries the same ladder
 * for the activity unit of analysis (never a breakdown).
 */
export const ACTIVITY_LEVEL_ID_TO_FILTER_KEY = {
  activity_k15: 'activities_level_1',
  activity_k50: 'activities_level_2',
  activity_k150: 'activities_level_3',
  activity_k500: 'activities_level_4',
  activity_k1500: 'activities_level_5',
  activity_k5000: 'activities_level_6',
} as const satisfies Readonly<
  Record<
    ActivityTaxonomySelectionListKey,
    keyof CompositionV3Filters & keyof TransitionsV3Filters
  >
>;

function isActivityTaxonomyLevelId(
  id: string
): id is ActivityTaxonomySelectionListKey {
  return id in ACTIVITY_LEVEL_ID_TO_FILTER_KEY;
}

export function appendActivitySelectionToFilters(
  acc: CompositionV3Filters | TransitionsV3Filters,
  levelId: string,
  numericId: number
): CompositionV3Filters | TransitionsV3Filters {
  if (!isActivityTaxonomyLevelId(levelId)) {
    return acc;
  }
  const key = ACTIVITY_LEVEL_ID_TO_FILTER_KEY[levelId];
  const prevRaw = acc[key];
  const prev = Array.isArray(prevRaw) ? prevRaw : [];
  return { ...acc, [key]: [...prev, numericId] };
}

/**
 * Activity tree level ids -> the numeric reference-taxonomy level the
 * expression-tree requests (talent-discovery `pos_activity`, posting-discovery
 * `activity`) carry alongside the id and taxonomy — the same 1..6 ladder the
 * `activities_level_*` keys encode positionally. Role/skill resolve their level
 * the same way (see `resolveRoleLevel`, `getSkillTierForLevelId`).
 */
const ACTIVITY_LEVEL_ID_TO_LEVEL = {
  activity_k15: 1,
  activity_k50: 2,
  activity_k150: 3,
  activity_k500: 4,
  activity_k1500: 5,
  activity_k5000: 6,
} as const satisfies Readonly<Record<ActivityTaxonomySelectionListKey, number>>;

export function getActivityLevelForLevelId(levelId: string): number | null {
  return isActivityTaxonomyLevelId(levelId)
    ? ACTIVITY_LEVEL_ID_TO_LEVEL[levelId]
    : null;
}

/** Activity tree level ids -> API `EntKind`, the same 1..6 ladder as above. */
const ACTIVITY_LEVEL_ID_TO_ENT_KIND = {
  activity_k15: EntKind.ActivityLvl_1,
  activity_k50: EntKind.ActivityLvl_2,
  activity_k150: EntKind.ActivityLvl_3,
  activity_k500: EntKind.ActivityLvl_4,
  activity_k1500: EntKind.ActivityLvl_5,
  activity_k5000: EntKind.ActivityLvl_6,
} as const satisfies Readonly<
  Record<ActivityTaxonomySelectionListKey, EntKind>
>;

export function getEntKindForActivityLevelId(levelId: string): EntKind | null {
  return isActivityTaxonomyLevelId(levelId)
    ? ACTIVITY_LEVEL_ID_TO_ENT_KIND[levelId]
    : null;
}

const GEO_AND_RICS_LEVEL_TO_ENT_KIND: Partial<
  Record<AllSelectionListsWithSkillTaxonomyKey, EntKind>
> = {
  rics_k10: EntKind.RicsK10,
  rics_k50: EntKind.RicsK50,
  rics_k400: EntKind.RicsK400,
  company: EntKind.Company,
  region: EntKind.Region,
  country: EntKind.Country,
  msa: EntKind.Msa,
  state: EntKind.State,
  county: EntKind.County,
  city: EntKind.City,
};

function isGeoOrRicsLevelId(
  id: string
): id is AllSelectionListsWithSkillTaxonomyKey {
  return id in GEO_AND_RICS_LEVEL_TO_ENT_KIND;
}

export function getEntKindForGeoOrRicsLevelId(levelId: string): EntKind | null {
  if (!isGeoOrRicsLevelId(levelId)) {
    return null;
  }
  return GEO_AND_RICS_LEVEL_TO_ENT_KIND[levelId] ?? null;
}

/** Maps compensation `TopEntity` / legacy `PositionsEntityKind` to composition V3 `EntKind`. */
export function positionsEntityKindToEntKind(
  kind: PositionsEntityKind
): EntKind {
  switch (kind) {
    case PositionsEntityKind.City:
      return EntKind.City;
    case PositionsEntityKind.Company:
      return EntKind.Company;
    case PositionsEntityKind.Country:
      return EntKind.Country;
    case PositionsEntityKind.Msa:
      return EntKind.Msa;
    case PositionsEntityKind.Region:
      return EntKind.Region;
    case PositionsEntityKind.RicsK10:
      return EntKind.RicsK10;
    case PositionsEntityKind.RicsK50:
      return EntKind.RicsK50;
    case PositionsEntityKind.RicsK400:
      return EntKind.RicsK400;
    case PositionsEntityKind.State:
      return EntKind.State;
    case PositionsEntityKind.RoleK7:
      return EntKind.RoleLvl_1;
    case PositionsEntityKind.RoleK150:
      return EntKind.RoleLvl_3;
    case PositionsEntityKind.RoleK1500:
      return EntKind.RoleLvl_5;
    case PositionsEntityKind.RoleK15000:
      return EntKind.RoleLvl_7;
  }
}

type PositionsEntitySelection = {
  EntID: number;
  EntType: PositionsEntityKind;
};

export const mapPositionsEntitiesToCompositionV3 = (
  entities: PositionsEntitySelection[] | undefined
): CompositionV3Ent[] | undefined => {
  if (!entities) {
    return undefined;
  }
  return entities.map((e) => ({
    EntID: e.EntID,
    EntType: positionsEntityKindToEntKind(e.EntType),
  }));
};

export function entKindToFilterKey(
  entType: EntKind
): keyof CompositionV3Filters | null {
  switch (entType) {
    case EntKind.Company:
      return 'company';
    case EntKind.Region:
      return 'region';
    case EntKind.Country:
      return 'country';
    case EntKind.State:
      return 'state';
    case EntKind.County:
      return 'county';
    case EntKind.Msa:
      return 'msa';
    case EntKind.City:
      return 'city';
    case EntKind.RicsK10:
      return 'rics_k10';
    case EntKind.RicsK50:
      return 'rics_k50';
    case EntKind.RicsK400:
      return 'rics_k400';
    case EntKind.RoleLvl_1:
      return 'role_level_1';
    case EntKind.RoleLvl_2:
      return 'role_level_2';
    case EntKind.RoleLvl_3:
      return 'role_level_3';
    case EntKind.RoleLvl_4:
      return 'role_level_4';
    case EntKind.RoleLvl_5:
      return 'role_level_5';
    case EntKind.RoleLvl_6:
      return 'role_level_6';
    case EntKind.RoleLvl_7:
      return 'role_level_7';
    case EntKind.SkillLvl_1:
      return 'skills_level_1';
    case EntKind.SkillLvl_2:
      return 'skills_level_2';
    case EntKind.SkillLvl_3:
      return 'skills_level_3';
    case EntKind.SkillLvl_4:
      return 'skills_level_4';
    case EntKind.SkillLvl_5:
      return 'skills_level_5';
    case EntKind.SkillLvl_6:
      return 'skills_level_6';
    case EntKind.SkillLvl_7:
      return 'skills_level_7';
    case EntKind.SkillLvl_8:
      return 'skills_level_8';
    case EntKind.ActivityLvl_1:
      return 'activities_level_1';
    case EntKind.ActivityLvl_2:
      return 'activities_level_2';
    case EntKind.ActivityLvl_3:
      return 'activities_level_3';
    case EntKind.ActivityLvl_4:
      return 'activities_level_4';
    case EntKind.ActivityLvl_5:
      return 'activities_level_5';
    case EntKind.ActivityLvl_6:
      return 'activities_level_6';
    default:
      return null;
  }
}

function removeIdFromNumericFilter(
  filters: CompositionV3Filters,
  key: keyof CompositionV3Filters,
  id: number
): CompositionV3Filters {
  const prevRaw = filters[key];
  if (!Array.isArray(prevRaw)) {
    return filters;
  }
  const nums = prevRaw.filter((x): x is number => typeof x === 'number');
  const next = nums.filter((n) => n !== id);
  if (next.length === nums.length) {
    return filters;
  }
  if (next.length === 0) {
    const { [key]: _removed, ...rest } = filters;
    return rest;
  }
  return { ...filters, [key]: next };
}

/**
 * Removes requested entity IDs from `filters` so they appear only in `entities`.
 */
export function stripEntitiesFromFilters(
  filters: CompositionV3Filters,
  entities: CompositionV3Ent[]
): CompositionV3Filters {
  return entities.reduce((acc, { EntType, EntID }) => {
    const key = entKindToFilterKey(EntType);
    if (key === null) {
      return acc;
    }
    return removeIdFromNumericFilter(acc, key, EntID);
  }, filters);
}
